/**
 * Composite skeleton loaders for high-frequency screens.
 *
 * Each variant mirrors the exact spacing/height of the real component so
 * the layout doesn't jump on data arrival. Uses the base `Skeleton`
 * primitive (Tailwind `animate-pulse`) so we get a subtle shimmer for free.
 */
import { Skeleton } from "./ui/skeleton";

// Slightly softer pulse — real data feels less "abrupt" landing on top.
const bar = "bg-slate-200/80";

/** Rows for a table/list screen (Projects, Quotes, Contacts). */
export function TableRowsSkeleton({ rows = 6, cols = 4 }) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="skeleton-table"
    >
      {/* Header */}
      <div className="hidden sm:flex items-center gap-4 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton
            key={i}
            className={`h-3 ${bar} ${i === 0 ? "w-32" : "w-20"}`}
          />
        ))}
      </div>
      {/* Rows */}
      <div className="divide-y divide-slate-100">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-4">
            <Skeleton className={`h-9 w-9 rounded-full ${bar} flex-shrink-0`} />
            <div className="flex-1 min-w-0 space-y-2">
              <Skeleton className={`h-3.5 w-48 max-w-full ${bar}`} />
              <Skeleton className={`h-3 w-32 max-w-full ${bar}`} />
            </div>
            <Skeleton className={`h-3 w-16 ${bar} hidden sm:block`} />
            <Skeleton className={`h-6 w-16 rounded-full ${bar} hidden md:block`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Contact-style list (avatar + two lines, no header). */
export function ContactListSkeleton({ rows = 6 }) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100"
      data-testid="skeleton-contact-list"
    >
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3 px-4 py-4">
          <Skeleton className={`h-10 w-10 rounded-full ${bar} flex-shrink-0`} />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className={`h-3.5 w-48 max-w-full ${bar}`} />
            <Skeleton className={`h-3 w-64 max-w-full ${bar}`} />
          </div>
          <Skeleton className={`h-6 w-14 rounded-full ${bar} hidden sm:block`} />
        </div>
      ))}
    </div>
  );
}

/** Kanban / Pipeline placeholder — 4 columns of stacked cards on desktop,
 *  a single vertical stack on mobile so it mirrors the real Pipeline. */
export function KanbanSkeleton({ columns = 4, cardsPerCol = 3 }) {
  return (
    <div data-testid="skeleton-kanban">
      {/* Desktop: horizontal columns */}
      <div className="hidden md:flex gap-4 overflow-hidden">
        {Array.from({ length: columns }).map((_, c) => (
          <div key={c} className="flex-1 min-w-[260px]">
            <div className="bg-slate-100/70 rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className={`h-3.5 w-24 ${bar}`} />
                <Skeleton className={`h-5 w-8 rounded-full ${bar}`} />
              </div>
              {Array.from({ length: cardsPerCol }).map((_, i) => (
                <KanbanCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Mobile: vertical stack */}
      <div className="md:hidden space-y-3">
        {Array.from({ length: cardsPerCol + 1 }).map((_, i) => (
          <KanbanCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function KanbanCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2 shadow-sm">
      <Skeleton className={`h-3.5 w-3/4 ${bar}`} />
      <Skeleton className={`h-3 w-1/2 ${bar}`} />
      <div className="flex items-center gap-2 pt-1">
        <Skeleton className={`h-5 w-16 rounded-full ${bar}`} />
        <Skeleton className={`h-5 w-12 rounded-full ${bar}`} />
      </div>
    </div>
  );
}

/** Cards grid (Suppliers, quote templates). */
export function CardGridSkeleton({ cards = 6 }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="skeleton-card-grid"
    >
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className={`h-10 w-10 rounded-lg ${bar}`} />
            <div className="flex-1 space-y-2">
              <Skeleton className={`h-3.5 w-32 ${bar}`} />
              <Skeleton className={`h-3 w-20 ${bar}`} />
            </div>
          </div>
          <Skeleton className={`h-3 w-full ${bar}`} />
          <Skeleton className={`h-3 w-2/3 ${bar}`} />
        </div>
      ))}
    </div>
  );
}

/** Compact tasks list. */
export function TaskListSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-3" data-testid="skeleton-task-list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
          <Skeleton className={`h-5 w-5 rounded ${bar} flex-shrink-0`} />
          <div className="flex-1 space-y-2">
            <Skeleton className={`h-3.5 w-2/3 ${bar}`} />
            <Skeleton className={`h-3 w-1/3 ${bar}`} />
          </div>
          <Skeleton className={`h-6 w-20 rounded-full ${bar} hidden sm:block`} />
        </div>
      ))}
    </div>
  );
}

/** Conversation list inside the Inbox left panel. */
export function InboxListSkeleton({ rows = 5 }) {
  return (
    <div data-testid="skeleton-inbox-list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-4 py-3 border-b border-slate-100">
          <div className="flex items-center justify-between gap-2 mb-2">
            <Skeleton className={`h-3.5 w-28 ${bar}`} />
            <Skeleton className={`h-3 w-10 ${bar}`} />
          </div>
          <Skeleton className={`h-3 w-52 max-w-full ${bar}`} />
        </div>
      ))}
    </div>
  );
}

/** Detail page (Project / Contact) — a title + tabs + card sections. */
export function DetailPageSkeleton() {
  return (
    <div className="min-h-screen p-4 sm:p-6 space-y-6" data-testid="skeleton-detail-page">
      <div className="space-y-2">
        <Skeleton className={`h-6 w-56 max-w-full ${bar}`} />
        <Skeleton className={`h-3 w-32 ${bar}`} />
      </div>
      <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className={`h-8 w-24 rounded-md ${bar}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <Skeleton className={`h-4 w-40 ${bar}`} />
            <Skeleton className={`h-3 w-full ${bar}`} />
            <Skeleton className={`h-3 w-5/6 ${bar}`} />
            <Skeleton className={`h-3 w-2/3 ${bar}`} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <Skeleton className={`h-4 w-32 ${bar}`} />
            <Skeleton className={`h-3 w-full ${bar}`} />
            <Skeleton className={`h-3 w-3/4 ${bar}`} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <Skeleton className={`h-4 w-24 ${bar}`} />
            <Skeleton className={`h-8 w-32 ${bar}`} />
            <Skeleton className={`h-3 w-full ${bar}`} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
            <Skeleton className={`h-4 w-28 ${bar}`} />
            <Skeleton className={`h-3 w-full ${bar}`} />
            <Skeleton className={`h-3 w-2/3 ${bar}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Dashboard KPI cards + a couple of widgets. */
export function DashboardSkeleton() {
  return (
    <div className="space-y-6" data-testid="skeleton-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <Skeleton className={`h-3 w-20 ${bar}`} />
            <Skeleton className={`h-7 w-24 ${bar}`} />
            <Skeleton className={`h-3 w-16 ${bar}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <Skeleton className={`h-4 w-36 ${bar}`} />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className={`h-9 w-9 rounded-full ${bar}`} />
              <div className="flex-1 space-y-2">
                <Skeleton className={`h-3 w-2/3 ${bar}`} />
                <Skeleton className={`h-3 w-1/3 ${bar}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <Skeleton className={`h-4 w-32 ${bar}`} />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className={`h-9 w-9 rounded-lg ${bar}`} />
              <div className="flex-1 space-y-2">
                <Skeleton className={`h-3 w-3/4 ${bar}`} />
                <Skeleton className={`h-3 w-1/2 ${bar}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
